import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import EnhancedSEO from '../../Components/SEO/EnhancedSEO';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Chip, 
  CardMedia,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloseIcon from '@mui/icons-material/Close';

const Demo = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const allVideos = [
    // Platform Introduction
    {
      id: 'intro-web',
      title: 'Revfer Introduction - Web',
      description: 'Discover what Revfer can do for your business',
      duration: '2:00',
      vimeoId: '1131768823',
      aspectRatio: '16/9',
      platform: 'Web',
      category: 'Platform Introduction',
      thumbnail: `https://vumbnail.com/1131768823.jpg`,
      showOnMobile: false
    },
    {
      id: 'intro-mobile',
      title: 'Revfer Introduction - Mobile',
      description: 'Discover what Revfer can do for your business',
      duration: '2:00',
      vimeoId: '1131768868',
      aspectRatio: '9/16',
      platform: 'Mobile',
      category: 'Platform Introduction',
      thumbnail: `https://vumbnail.com/1131768868.jpg`,
      showOnMobile: true
    },

    // Account Creation
    {
      id: 'account-create-web',
      title: 'Account Creation - Web',
      description: 'Learn how to create your Revfer account on desktop',
      duration: '2:30',
      vimeoId: '1119856272',
      aspectRatio: '16/9',
      platform: 'Web',
      category: 'Getting Started',
      thumbnail: `https://vumbnail.com/1119856272.jpg`,
      showOnMobile: false
    },
    {
      id: 'account-create-mobile',
      title: 'Account Creation - Mobile',
      description: 'Create your Revfer account on-the-go',
      duration: '2:00',
      vimeoId: '1119857073',
      aspectRatio: '9/16',
      platform: 'Mobile',
      category: 'Getting Started',
      thumbnail: `https://vumbnail.com/1119857073.jpg`,
      showOnMobile: true
    },

    // Business Creation Demos
    {
      id: 'web-create-demo',
      title: 'Create New Business - Web Platform',
      description: 'See how to set up your business using our web dashboard',
      duration: '3:45',
      vimeoId: '1114350858',
      aspectRatio: '16/9',
      platform: 'Web',
      category: 'Business Setup',
      thumbnail: `https://vumbnail.com/1114350858.jpg`,
      showOnMobile: false
    },
    {
      id: 'mobile-create-demo',
      title: 'Create New Business - Mobile App',
      description: 'Learn to launch your business on-the-go with our mobile app',
      duration: '2:30',
      vimeoId: '1114350841',
      aspectRatio: '9/16',
      platform: 'Mobile',
      category: 'Business Setup',
      thumbnail: `https://vumbnail.com/1114350841.jpg`,
      showOnMobile: true
    },

    // Business Management Demos
    {
      id: 'web-manage-demo',
      title: 'Business Dashboard & Tools - Web Platform',
      description: 'Explore business analytics, AI tools, and management features',
      duration: '4:15',
      vimeoId: '1116389481', 
      aspectRatio: '16/9',
      platform: 'Web',
      category: 'Business Management',
      thumbnail: `https://vumbnail.com/1116389481.jpg`,
      showOnMobile: false
    },
    {
      id: 'mobile-manage-demo',
      title: 'Business Dashboard & Tools - Mobile App',
      description: 'Manage your business anywhere with mobile analytics and AI features',
      duration: '3:00',
      vimeoId: '1116389491', 
      aspectRatio: '9/16',
      platform: 'Mobile',
      category: 'Business Management',
      thumbnail: `https://vumbnail.com/1116389491.jpg`,
      showOnMobile: true
    },

    // AI Features - Voice Notifications
    {
      id: 'voice-notifications-web',
      title: 'Voice Notifications - Web',
      description: 'Record voice messages for customers - AI displays them on your business page',
      duration: '2:30',
      vimeoId: '1118093236',
      aspectRatio: '16/9',
      platform: 'Web',
      category: 'AI Features',
      thumbnail: `https://vumbnail.com/1118093236.jpg`,
      showOnMobile: false
    },
    {
      id: 'voice-notifications-mobile',
      title: 'Voice Notifications - Mobile',
      description: 'Quickly inform customers with your voice - AI converts to text automatically',
      duration: '2:00',
      vimeoId: '1118093260',
      aspectRatio: '9/16',
      platform: 'Mobile',
      category: 'AI Features',
      thumbnail: `https://vumbnail.com/1118093260.jpg`,
      showOnMobile: true
    },

    // Trust & Discovery - Trust Chain References
    {
      id: 'trust-chain-refs-web',
      title: 'Business References via Trust Chains - Web',
      description: 'Find trusted businesses through your known network - digital word of mouth',
      duration: '3:00',
      vimeoId: '1122362567',
      aspectRatio: '16/9',
      platform: 'Web',
      category: 'Trust & Discovery',
      thumbnail: `https://vumbnail.com/1122362567.jpg`,
      showOnMobile: false
    },
    {
      id: 'trust-chain-refs-mobile',
      title: 'Business References via Trust Chains - Mobile',
      description: 'Discover businesses recommended by people you trust',
      duration: '2:30',
      vimeoId: '1122362593',
      aspectRatio: '9/16',
      platform: 'Mobile',
      category: 'Trust & Discovery',
      thumbnail: `https://vumbnail.com/1122362593.jpg`,
      showOnMobile: true
    },

    // Trust Chain Explanations
    {
      id: 'trust-chain-explain-web',
      title: 'Trust Chain Explained - Web',
      description: 'Deep dive into how trust chains connect you to verified businesses',
      duration: '3:30',
      vimeoId: '1127295796',
      aspectRatio: '16/9',
      platform: 'Web',
      category: 'Trust & Discovery',
      thumbnail: `https://vumbnail.com/1127295796.jpg`,
      showOnMobile: false
    },
    {
      id: 'trust-chain-explain-mobile',
      title: 'Trust Chain Explained - Mobile',
      description: 'Understand how trust chains work on Revfer',
      duration: '3:00',
      vimeoId: '1127295818',
      aspectRatio: '9/16',
      platform: 'Mobile',
      category: 'Trust & Discovery',
      thumbnail: `https://vumbnail.com/1127295818.jpg`,
      showOnMobile: true
    },

    // Trust Score Explanation
    {
      id: 'trust-score-web',
      title: 'Trust Score Explained - Web',
      description: 'Learn how trust scores help you find reliable businesses',
      duration: '2:30',
      vimeoId: '1124445685',
      aspectRatio: '16/9',
      platform: 'Web',
      category: 'Trust & Discovery',
      thumbnail: `https://vumbnail.com/1124445685.jpg`,
      showOnMobile: false
    },
    {
      id: 'trust-score-mobile',
      title: 'Trust Score Explained - Mobile',
      description: 'Understanding trust scores on Revfer',
      duration: '2:00',
      vimeoId: '1124445666',
      aspectRatio: '9/16',
      platform: 'Mobile',
      category: 'Trust & Discovery',
      thumbnail: `https://vumbnail.com/1124445666.jpg`,
      showOnMobile: true
    },

    // Smart vs Traditional Ratings
    {
      id: 'smart-ratings-web',
      title: 'Smart vs Traditional Ratings - Web',
      description: 'See why smart ratings outperform traditional star ratings',
      duration: '3:00',
      vimeoId: '1125654476',
      aspectRatio: '16/9',
      platform: 'Web',
      category: 'Trust & Discovery',
      thumbnail: `https://vumbnail.com/1125654476.jpg`,
      showOnMobile: false
    },
    {
      id: 'smart-ratings-mobile',
      title: 'Smart vs Traditional Ratings - Mobile',
      description: 'Discover the power of smart ratings',
      duration: '2:30',
      vimeoId: '1125654510',
      aspectRatio: '9/16',
      platform: 'Mobile',
      category: 'Trust & Discovery',
      thumbnail: `https://vumbnail.com/1125654510.jpg`,
      showOnMobile: true
    },

    // Reference Percentage
    {
      id: 'reference-pct-web',
      title: 'Reference Percentage - Web',
      description: 'Track how often your business gets reviews from connected users',
      duration: '2:30',
      vimeoId: '1128906639',
      aspectRatio: '16/9',
      platform: 'Web',
      category: 'Trust & Discovery',
      thumbnail: `https://vumbnail.com/1128906639.jpg`,
      showOnMobile: false
    },
    {
      id: 'reference-pct-mobile',
      title: 'Reference Percentage - Mobile',
      description: 'Measure your community engagement with reference percentage',
      duration: '2:00',
      vimeoId: '1128906659',
      aspectRatio: '9/16',
      platform: 'Mobile',
      category: 'Trust & Discovery',
      thumbnail: `https://vumbnail.com/1128906659.jpg`,
      showOnMobile: true
    },

    // Email Invites
    {
      id: 'email-invites-web',
      title: 'Email Invites - Web',
      description: 'Invite friends and colleagues to join Revfer',
      duration: '2:00',
      vimeoId: '1122362125',
      aspectRatio: '16/9',
      platform: 'Web',
      category: 'Growing Your Network',
      thumbnail: `https://vumbnail.com/1122362125.jpg`,
      showOnMobile: false
    },
    {
      id: 'email-invites-mobile',
      title: 'Email Invites - Mobile',
      description: 'Grow your network by inviting others to Revfer',
      duration: '1:30',
      vimeoId: '1122362147',
      aspectRatio: '9/16',
      platform: 'Mobile',
      category: 'Growing Your Network',
      thumbnail: `https://vumbnail.com/1122362147.jpg`,
      showOnMobile: true
    },

    // ROI Calculator - Short Videos
    {
      id: 'roi-short-web',
      title: 'ROI Calculator Quick Overview - Web',
      description: 'See your potential savings in under a minute',
      duration: '1:00',
      vimeoId: '1134773266',
      aspectRatio: '16/9',
      platform: 'Web',
      category: 'ROI & Value',
      thumbnail: `https://vumbnail.com/1134773266.jpg`,
      showOnMobile: false
    },
    {
      id: 'roi-short-mobile',
      title: 'ROI Calculator Quick Overview - Mobile',
      description: 'Calculate your savings on the go',
      duration: '1:00',
      vimeoId: '1134773281',
      aspectRatio: '9/16',
      platform: 'Mobile',
      category: 'ROI & Value',
      thumbnail: `https://vumbnail.com/1134773281.jpg`,
      showOnMobile: true
    },

    // ROI Calculator - Long Videos
    {
      id: 'roi-long-web',
      title: 'ROI Calculator Deep Dive - Web',
      description: 'Comprehensive walkthrough of ROI calculations and savings breakdown',
      duration: '4:00',
      vimeoId: '1134773238',
      aspectRatio: '16/9',
      platform: 'Web',
      category: 'ROI & Value',
      thumbnail: `https://vumbnail.com/1134773238.jpg`,
      showOnMobile: false
    },
    {
      id: 'roi-long-mobile',
      title: 'ROI Calculator Deep Dive - Mobile',
      description: 'Full breakdown of your potential ROI with Revfer',
      duration: '3:30',
      vimeoId: '1134773209',
      aspectRatio: '9/16',
      platform: 'Mobile',
      category: 'ROI & Value',
      thumbnail: `https://vumbnail.com/1134773209.jpg`,
      showOnMobile: true
    }
  ];

  // Filter videos based on device
  const videos = allVideos.filter(video => 
    isMobile ? video.showOnMobile : true
  );

  // Define category order for consistent display
  const categoryOrder = [
    'Platform Introduction',
    'Getting Started',
    'Business Setup',
    'Business Management',
    'AI Features',
    'Trust & Discovery',
    'Growing Your Network',
    'ROI & Value'
  ];

  // Group videos by category for better organization
  const videosByCategory = videos.reduce((acc, video) => {
    if (!acc[video.category]) {
      acc[video.category] = [];
    }
    acc[video.category].push(video);
    return acc;
  }, {});

  // Sort categories according to defined order
  const sortedCategories = categoryOrder.filter(cat => videosByCategory[cat]);

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
  };

  const handleCloseVideo = () => {
    setSelectedVideo(null);
  };

  const getModalProps = (video) => {
    const isMobileVideo = video?.aspectRatio === '9/16';
    const isScreenMobile = isMobile;

    if (isMobileVideo) {
      return {
        maxWidth: isScreenMobile ? 'sm' : 'md',
        fullScreen: isScreenMobile,
        videoContainerStyle: {
          width: '100%',
          maxHeight: isScreenMobile ? '100vh' : '70vh',
          aspectRatio: '9/16',
          overflow: 'hidden'
        }
      };
    } else {
      return {
        maxWidth: 'lg',
        fullScreen: false,
        videoContainerStyle: {
          width: '100%',
          aspectRatio: '16/9'
        }
      };
    }
  };

  const modalProps = getModalProps(selectedVideo);

  const getCategoryColor = (category) => {
    const colors = {
      'Platform Introduction': '#9C27B0',
      'Getting Started': '#2196F3',
      'Business Setup': '#4CAF50',
      'Business Management': '#FF9800',
      'AI Features': '#E91E63',
      'Trust & Discovery': '#00BCD4',
      'Growing Your Network': '#8BC34A',
      'ROI & Value': '#FF5722'
    };
    return colors[category] || '#757575';
  };

  const getCategoryDescription = (category) => {
    const descriptions = {
      'Platform Introduction': 'Get to know Revfer and what it can do for you',
      'Getting Started': 'Create your account and get set up',
      'Business Setup': 'Launch your business on the platform',
      'Business Management': 'Manage your business with powerful tools',
      'AI Features': 'Leverage AI to automate and enhance your business',
      'Trust & Discovery': 'Understand how trust-based discovery works',
      'Growing Your Network': 'Expand your reach and community',
      'ROI & Value': 'See the real value Revfer brings to your business'
    };
    return descriptions[category] || '';
  };

  // Helper to convert duration string to ISO 8601 format
  const durationToISO = (duration) => {
    const [mins, secs] = duration.split(':').map(Number);
    return `PT${mins}M${secs}S`;
  };

  // SEO Structured Data - FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How do I get started with Revfer?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Getting started with Revfer is simple. Watch our account creation tutorial to set up your account in minutes, then follow the business setup guide to launch your business profile with AI-powered tools."
        }
      },
      {
        "@type": "Question",
        "name": "What is a Trust Chain and how does it work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Trust Chains are Revfer's unique way of connecting you to businesses through people you know. Instead of anonymous reviews, you see recommendations from your network - like digital word of mouth. Watch our Trust Chain explainer videos to learn more."
        }
      },
      {
        "@type": "Question",
        "name": "How does the Smart Rating system differ from traditional star ratings?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Smart Ratings weight reviews based on the reviewer's credibility and your connection to them through trust chains. This means recommendations from people in your network carry more weight than anonymous reviews, giving you more reliable business insights."
        }
      },
      {
        "@type": "Question",
        "name": "What AI features does Revfer offer for businesses?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Revfer offers AI-powered customer service automation, voice notifications that convert to text, smart content generation, review management, and business analytics. These features help you save time and provide better customer experiences."
        }
      },
      {
        "@type": "Question",
        "name": "How can I calculate my potential ROI with Revfer?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Use our ROI Calculator to see your potential savings. Most businesses save an average of $5,100 per month through reduced customer service costs and automation. Watch our ROI calculator tutorials for a complete walkthrough."
        }
      },
      {
        "@type": "Question",
        "name": "Can I use Revfer on my mobile device?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! Revfer is fully optimized for mobile. You can create and manage your business, view analytics, send voice notifications, and discover trusted businesses all from your phone. We provide dedicated mobile tutorials for all features."
        }
      }
    ]
  };

  // VideoObject Schema for featured videos (web versions)
  const videoListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Revfer Platform Tutorial Videos",
    "description": "Complete video guide to using Revfer's AI-powered business platform and trust-based discovery features",
    "numberOfItems": allVideos.filter(v => v.platform === 'Web').length,
    "itemListElement": allVideos
      .filter(v => v.platform === 'Web')
      .map((video, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "VideoObject",
          "name": video.title,
          "description": video.description,
          "thumbnailUrl": video.thumbnail,
          "contentUrl": `https://vimeo.com/${video.vimeoId}`,
          "embedUrl": `https://player.vimeo.com/video/${video.vimeoId}`,
          "duration": durationToISO(video.duration),
          "uploadDate": "2024-01-01T00:00:00Z",
          "publisher": {
            "@type": "Organization",
            "name": "Revfer",
            "logo": {
              "@type": "ImageObject",
              "url": "https://www.revfer.com/logo.png"
            }
          }
        }
      }))
  };

  // How-To Schema for getting started
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Get Started with Revfer",
    "description": "Step-by-step video guide to setting up your business on Revfer's AI-powered platform",
    "image": "https://www.revfer.com/images/demo-og.jpg",
    "totalTime": "PT15M",
    "step": [
      {
        "@type": "HowToStep",
        "name": "Create Your Account",
        "text": "Sign up for Revfer and create your personal account to get started.",
        "url": "https://www.revfer.com/demo#account-create-web",
        "video": {
          "@type": "VideoObject",
          "name": "Account Creation Tutorial",
          "contentUrl": "https://vimeo.com/1119856272",
          "thumbnailUrl": "https://vumbnail.com/1119856272.jpg"
        }
      },
      {
        "@type": "HowToStep",
        "name": "Set Up Your Business",
        "text": "Create your business profile and configure AI-powered features.",
        "url": "https://www.revfer.com/demo#web-create-demo",
        "video": {
          "@type": "VideoObject",
          "name": "Business Setup Tutorial",
          "contentUrl": "https://vimeo.com/1114350858",
          "thumbnailUrl": "https://vumbnail.com/1114350858.jpg"
        }
      },
      {
        "@type": "HowToStep",
        "name": "Explore Your Dashboard",
        "text": "Learn to use your business dashboard, analytics, and AI tools.",
        "url": "https://www.revfer.com/demo#web-manage-demo",
        "video": {
          "@type": "VideoObject",
          "name": "Dashboard & Tools Tutorial",
          "contentUrl": "https://vimeo.com/1116389481",
          "thumbnailUrl": "https://vumbnail.com/1116389481.jpg"
        }
      },
      {
        "@type": "HowToStep",
        "name": "Grow Your Network",
        "text": "Invite customers and build your trust chain network.",
        "url": "https://www.revfer.com/demo#email-invites-web",
        "video": {
          "@type": "VideoObject",
          "name": "Email Invites Tutorial",
          "contentUrl": "https://vimeo.com/1122362125",
          "thumbnailUrl": "https://vumbnail.com/1122362125.jpg"
        }
      }
    ]
  };

  return (
    <>
      {/* Enhanced SEO */}
      <EnhancedSEO 
        title="Video Tutorials & Platform Demos - Learn Revfer AI Tools | Revfer"
        description="Watch step-by-step video tutorials for Revfer's AI business platform. Learn about trust chains, smart ratings, voice notifications, ROI calculator, and more. Available for web and mobile."
        keywords="Revfer demo, Revfer tutorial, AI business platform demo, trust chain tutorial, smart ratings explained, business AI tutorial, Revfer how to, voice notifications demo, ROI calculator tutorial, local business discovery demo, small business AI guide, Revfer walkthrough, trust score explained, reference percentage"
        type="website"
        image="/images/demo-og.jpg"
        author="Revfer"
        dateModified={new Date().toISOString()}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Demo & Tutorials', url: '/demo' }
        ]}
      />

      {/* Structured Data */}
      <Helmet>
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>

        {/* Video List Schema */}
        <script type="application/ld+json">
          {JSON.stringify(videoListSchema)}
        </script>

        {/* How-To Schema */}
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>

        {/* Mobile viewport optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes" />
        
        {/* Demo page specific meta tags */}
        <meta name="content-type" content="Video Tutorial, Demo, How-To Guide" />
        <meta name="target-audience" content="Small Business Owners, Entrepreneurs, Local Business Managers, Consumers" />
        <meta name="video-count" content={allVideos.length} />
        <meta name="category-count" content={categoryOrder.length} />
        
        {/* Platform coverage */}
        <meta name="platforms-covered" content="Web Platform, Mobile App, iOS, Android" />
        <meta name="features-demonstrated" content="Trust Chains, Smart Ratings, Voice Notifications, ROI Calculator, Business Setup, Email Invites, Trust Score, Reference Percentage" />
        
        {/* Learning objectives */}
        <meta name="learning-outcomes" content="Account Setup, Business Creation, AI Feature Usage, Network Growth, Trust System Understanding, ROI Calculation" />
        <meta name="skill-level" content="Beginner Friendly, No Technical Skills Required" />
        
        {/* Video metadata */}
        <meta name="video-format" content="Tutorial, Walkthrough, Feature Demo, Explainer" />
        <meta name="video-duration-range" content="1-4 minutes" />
        <meta name="video-host" content="Vimeo" />
        
        {/* Open Graph Video Tags */}
        <meta property="og:video" content="https://vimeo.com/1131768823" />
        <meta property="og:video:type" content="text/html" />
        <meta property="og:video:width" content="1280" />
        <meta property="og:video:height" content="720" />
        
        {/* Twitter Video Card */}
        <meta name="twitter:card" content="player" />
        <meta name="twitter:player" content="https://player.vimeo.com/video/1131768823" />
        <meta name="twitter:player:width" content="1280" />
        <meta name="twitter:player:height" content="720" />
      </Helmet>

      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3, mt: { xs: 8, md: 10 } }}>
        <Typography variant="h3" component="h1" gutterBottom align="center" sx={{ mb: 2 }}>
          See Revfer in Action
        </Typography>
        
        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 2 }}>
          {isMobile 
            ? "Watch how to launch and manage your business on mobile"
            : "Watch how easy it is to launch your business and manage it with AI-powered tools"
          }
        </Typography>

        <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 6 }}>
          {videos.length} tutorial videos • {sortedCategories.length} categories
        </Typography>

        {/* Render videos by category */}
        {sortedCategories.map((category) => (
          <Box key={category} sx={{ mb: 6 }} id={category.toLowerCase().replace(/\s+/g, '-')}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography 
                variant="h5" 
                component="h2" 
                sx={{ 
                  color: getCategoryColor(category),
                  fontWeight: 600
                }}
              >
                {category}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {getCategoryDescription(category)}
              </Typography>
            </Box>
            
            <Grid container spacing={2} justifyContent="center">
              {videosByCategory[category].map((video) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={video.id}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      border: `2px solid transparent`,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3,
                        borderColor: getCategoryColor(category)
                      }
                    }}
                    onClick={() => handleVideoClick(video)}
                    id={video.id}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="140"
                        image={video.thumbnail}
                        alt={video.title}
                        sx={{ 
                          bgcolor: '#000',
                          objectFit: 'cover'
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          bgcolor: 'rgba(0,0,0,0.7)',
                          borderRadius: '50%',
                          width: 48,
                          height: 48,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: getCategoryColor(category),
                            transform: 'translate(-50%, -50%) scale(1.1)'
                          }
                        }}
                      >
                        <PlayArrowIcon sx={{ color: 'white', fontSize: 28 }} />
                      </Box>
                      
                      <Chip
                        label={video.duration}
                        size="small"
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8,
                          bgcolor: 'rgba(0,0,0,0.8)',
                          color: 'white',
                          fontSize: '0.75rem'
                        }}
                      />

                      {/* Category badge */}
                      <Chip
                        label={category}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          bgcolor: getCategoryColor(category),
                          color: 'white',
                          fontSize: '0.65rem',
                          height: 20,
                          maxWidth: '70%',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }
                        }}
                      />
                    </Box>
                    
                    <CardContent sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                        <Typography 
                          variant="subtitle1" 
                          component="h3" 
                          sx={{ 
                            fontWeight: 600, 
                            fontSize: '0.95rem',
                            flexGrow: 1
                          }}
                        >
                          {video.title}
                        </Typography>
                        <Chip 
                          label={video.platform} 
                          size="small" 
                          color={video.platform === 'Web' ? 'primary' : 'secondary'}
                          sx={{ fontSize: '0.7rem', height: 20, flexShrink: 0 }}
                        />
                      </Box>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ fontSize: '0.85rem', flexGrow: 1 }}
                      >
                        {video.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}

        {/* Show platform info for mobile users */}
        {isMobile && (
          <Box sx={{ mt: 4, p: 3, bgcolor: 'rgba(25, 118, 210, 0.04)', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              Want to see our web platform demos? Visit this page on desktop for the complete experience.
            </Typography>
          </Box>
        )}

        <Dialog
          open={Boolean(selectedVideo)}
          onClose={handleCloseVideo}
          maxWidth={modalProps.maxWidth}
          fullWidth
          fullScreen={modalProps.fullScreen}
          PaperProps={{
            sx: {
              bgcolor: '#000',
              borderRadius: modalProps.fullScreen ? 0 : 2,
              margin: modalProps.fullScreen ? 0 : 'auto'
            }
          }}
        >
          {selectedVideo && (
            <>
              <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                color: 'white',
                bgcolor: '#000',
                py: modalProps.fullScreen ? 2 : 1
              }}>
                <Box>
                  <Typography variant={modalProps.fullScreen ? "h6" : "subtitle1"}>
                    {selectedVideo.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {selectedVideo.category}
                  </Typography>
                </Box>
                <IconButton onClick={handleCloseVideo} sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 0, bgcolor: '#000', display: 'flex', justifyContent: 'center' }}>
                <Box
                  sx={{
                    position: 'relative',
                    ...modalProps.videoContainerStyle,
                    '& iframe': {
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: 'none'
                    }
                  }}
                >
                  <iframe 
                    src={`https://player.vimeo.com/video/${selectedVideo.vimeoId}?badge=0&autopause=0&controls=1&playsinline=1&autoplay=1`}
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title={selectedVideo.title}
                  />
                </Box>
              </DialogContent>
            </>
          )}
        </Dialog>
      </Box>
    </>
  );
};

export default Demo;